import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const SERVICE_HANDLES = new Set([
  'express-service-48-stunden',
  'hinversandlabel',
  'ruckversand-mit-sendungsverfolgung',
]);
const EXPRESS_HANDLE = 'express-service-48-stunden';
const RETURN_HANDLE = 'ruckversand-mit-sendungsverfolgung';
const PROMPT_PROPERTY = '_reparablo_express_prompted';
const promptedCarts = (window.__reparabloPromptedCarts ||= new Set());

class CartServices extends HTMLElement {
  #busy = false;
  #legalBusy = false;
  #visibilityTimer;

  connectedCallback() {
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleLegalChange);
    document.addEventListener('click', this.#guardCheckout, true);
    document.addEventListener('submit', this.#guardCheckout, true);
    this.#initializeLegalConsent();
    this.#initialize();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleLegalChange);
    document.removeEventListener('click', this.#guardCheckout, true);
    document.removeEventListener('submit', this.#guardCheckout, true);
    window.clearTimeout(this.#visibilityTimer);
  }

  #initialize = async () => {
    this.#setCheckoutLocked(true);

    try {
      if (!window.__reparabloCartNormalization) {
        window.__reparabloCartNormalization = this.#normalizeCart().finally(() => {
          window.__reparabloCartNormalization = null;
        });
      }

      await window.__reparabloCartNormalization;
      const cart = await this.#fetchCart();
      const repairItems = cart.items.filter((item) => !SERVICE_HANDLES.has(this.#itemHandle(item)));
      const hasReturn = cart.items.some((item) => this.#itemHandle(item) === RETURN_HANDLE && item.quantity === 1);

      if (repairItems.length > 0 && hasReturn) {
        this.#setCheckoutLocked(false);
        this.#scheduleExpressPrompt(cart);
      } else if (repairItems.length === 0) {
        this.#setCheckoutLocked(false);
      }
    } catch (error) {
      this.#setCheckoutLocked(false);
      this.#showError(error, 'Die Versandoptionen konnten nicht vorbereitet werden.');
    }
  };

  #normalizeCart = async () => {
    const cart = await this.#fetchCart();
    const repairItems = cart.items.filter((item) => !SERVICE_HANDLES.has(this.#itemHandle(item)));
    const serviceItems = cart.items.filter((item) => SERVICE_HANDLES.has(this.#itemHandle(item)));

    if (repairItems.length === 0) {
      if (serviceItems.length === 0) return;
      const updates = Object.fromEntries(serviceItems.map((item) => [item.key, 0]));
      await this.#updateMultiple(updates, serviceItems, 'remove');
      return;
    }

    const returnItem = serviceItems.find((item) => this.#itemHandle(item) === RETURN_HANDLE);
    if (!returnItem) {
      const returnVariantId = this.dataset.returnVariantId;
      if (!returnVariantId) throw new Error('Der Rückversand ist derzeit nicht verfügbar.');
      await this.#mutateLine('add', returnVariantId, 'return-shipping');
      return;
    }

    const quantityUpdates = {};
    for (const item of serviceItems) {
      if (item.quantity !== 1) quantityUpdates[item.key] = 1;
    }
    if (Object.keys(quantityUpdates).length > 0) await this.#updateMultiple(quantityUpdates, serviceItems, 'update');
  };

  #handleSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-cart-service-form]')) return;
    event.preventDefault();
    if (this.#busy) return;

    const form = event.target;
    const action = form.dataset.action === 'remove' ? 'remove' : 'add';
    const service = form.dataset.service || 'service';
    const itemId = String(new FormData(form).get('id') || '');
    if (!itemId) return;

    this.#busy = true;
    this.#setFormBusy(form, true);
    this.#clearErrors();

    try {
      await this.#mutateLine(action, itemId, service);
      if (form.hasAttribute('data-close-on-success')) {
        const dialog = form.closest('dialog');
        if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close('accepted');
      }
    } catch (error) {
      this.#showError(error, 'Die Auswahl konnte nicht aktualisiert werden.');
    } finally {
      this.#busy = false;
      if (form.isConnected) this.#setFormBusy(form, false);
    }
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const declineButton = event.target.closest('[data-express-decline]');
    if (!declineButton) return;
    const dialog = declineButton.closest('dialog');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close('declined');
  };

  #handleLegalChange = async (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('[data-legal-master], [data-legal-item]')) return;
    if (this.#legalBusy) return;

    const items = this.#legalItems();
    if (event.target.matches('[data-legal-master]')) {
      items.forEach((input) => {
        input.checked = event.target.checked;
      });
    }

    const state = this.#legalState(items);
    this.#broadcastLegalState(state);
    this.#setLegalLocked(true);
    this.#setLegalBusy(true);
    this.#clearLegalError();

    try {
      await this.#postCart('update', { attributes: state.attributes });
      this.#setLegalLocked(!state.complete);
      this.#broadcastLegalState(state);
    } catch (error) {
      this.#setLegalLocked(true);
      this.#showLegalError(error);
    } finally {
      this.#setLegalBusy(false);
    }
  };

  #guardCheckout = (event) => {
    const isSyncing = document.documentElement.classList.contains('reparablo-cart-syncing');
    const needsLegalConsent = document.documentElement.classList.contains('reparablo-legal-pending');
    if (!isSyncing && !needsLegalConsent) return;
    if (!(event.target instanceof Element)) return;
    const checkoutTarget = event.target.closest('[name="checkout"], .cart__checkout-button, .additional-checkout-buttons');
    if (!checkoutTarget) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (needsLegalConsent) this.#showLegalError();
  };

  #initializeLegalConsent() {
    const items = this.#legalItems();
    if (items.length === 0) {
      this.#setLegalLocked(false);
      return;
    }

    const state = this.#legalState(items);
    this.#broadcastLegalState(state);
    this.#setLegalLocked(!state.complete);
  }

  #legalItems() {
    return Array.from(this.querySelectorAll('[data-legal-item]')).filter((input) => input instanceof HTMLInputElement);
  }

  #legalState(items = this.#legalItems()) {
    const attributes = {};
    for (const input of items) {
      const attribute = input.dataset.cartAttribute;
      if (!attribute) continue;
      attributes[attribute] = input.checked ? (input.dataset.cartValue || 'Bestätigt') : '';
    }

    return {
      attributes,
      checked: Object.fromEntries(items.map((input) => [input.dataset.legalKey || '', input.checked])),
      complete: items.length > 0 && items.every((input) => input.checked),
    };
  }

  #broadcastLegalState(state) {
    document.querySelectorAll('[data-legal-item]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const key = input.dataset.legalKey || '';
      input.checked = Boolean(state.checked[key]);
    });

    document.querySelectorAll('[data-legal-consent]').forEach((container) => {
      const inputs = Array.from(container.querySelectorAll('[data-legal-item]')).filter((input) => input instanceof HTMLInputElement);
      const complete = inputs.length > 0 && inputs.every((input) => input.checked);
      const selected = inputs.filter((input) => input.checked).length;
      const master = container.querySelector('[data-legal-master]');
      if (master instanceof HTMLInputElement) {
        master.checked = complete;
        master.indeterminate = selected > 0 && !complete;
      }
      container.classList.toggle('cart-services__legal--complete', complete);
    });
  }

  #scheduleExpressPrompt(cart) {
    const hasExpress = cart.items.some((item) => this.#itemHandle(item) === EXPRESS_HANDLE);
    if (hasExpress) return;

    const returnItem = cart.items.find((item) => this.#itemHandle(item) === RETURN_HANDLE);
    if (!returnItem || returnItem.properties?.[PROMPT_PROPERTY] === '1') return;

    const cartKey = String(cart.token || this.dataset.cartToken || 'current-cart');
    if (promptedCarts.has(cartKey)) return;

    let attempts = 0;
    const tryOpen = async () => {
      if (!this.isConnected || attempts >= 480) return;
      attempts += 1;
      const dialog = this.querySelector('[data-express-prompt]');
      if (!(dialog instanceof HTMLDialogElement)) return;

      if (!this.#isCartSurfaceVisible()) {
        this.#visibilityTimer = window.setTimeout(tryOpen, 250);
        return;
      }

      if (promptedCarts.has(cartKey)) return;
      promptedCarts.add(cartKey);

      try {
        await this.#markPromptedForCart(returnItem);
      } catch {
        promptedCarts.add(cartKey);
      }

      if (!dialog.open) dialog.showModal();
    };

    this.#visibilityTimer = window.setTimeout(tryOpen, 120);
  }

  #isCartSurfaceVisible() {
    const cartPath = (window.Theme?.routes?.cart_url || '/cart').replace(/\/$/, '');
    if (window.location.pathname.replace(/\/$/, '') === cartPath) return true;

    const rect = this.getBoundingClientRect();
    const styles = window.getComputedStyle(this);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && Number(styles.opacity || 1) > 0 &&
      rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  async #markPromptedForCart(returnItem) {
    await this.#postCart('change', {
      id: returnItem.key,
      quantity: 1,
      properties: { ...(returnItem.properties || {}), [PROMPT_PROPERTY]: '1' },
    });
  }

  async #mutateLine(action, itemId, source) {
    const sectionIds = this.#sectionIds();
    const deferredUpdate = CartLinesUpdateEvent.createPromise();
    const isAdd = action === 'add';
    this.dispatchEvent(new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: isAdd ? [{ merchandiseId: itemId, quantity: 1 }] : [{ id: itemId, quantity: 0 }],
      promise: deferredUpdate.promise,
    }));

    const payload = isAdd
      ? { items: [{ id: Number(itemId), quantity: 1 }], sections: sectionIds, sections_url: window.location.pathname }
      : { id: itemId, quantity: 0, sections: sectionIds, sections_url: window.location.pathname };

    try {
      const responseData = await this.#postCart(isAdd ? 'add' : 'change', payload);
      const cart = await this.#fetchCart();
      deferredUpdate.resolve(this.#eventResult(cart, responseData.sections, source));
      return cart;
    } catch (error) {
      deferredUpdate.reject(error);
      this.#dispatchCartError(error);
      throw error;
    }
  }

  async #updateMultiple(updates, items, action) {
    const sectionIds = this.#sectionIds();
    const deferredUpdate = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: items.map((item) => ({ id: item.key, quantity: Number(updates[item.key] ?? item.quantity) })),
      promise: deferredUpdate.promise,
    }));

    try {
      const responseData = await this.#postCart('update', { updates, sections: sectionIds, sections_url: window.location.pathname });
      const cart = await this.#fetchCart();
      deferredUpdate.resolve(this.#eventResult(cart, responseData.sections, 'cart-services-normalization'));
      return cart;
    } catch (error) {
      deferredUpdate.reject(error);
      this.#dispatchCartError(error);
      throw error;
    }
  }

  async #postCart(action, payload) {
    const routes = window.Theme?.routes || {};
    const fallbackRoot = window.Shopify?.routes?.root || '/';
    const endpoints = {
      add: routes.cart_add_url || `${fallbackRoot}cart/add.js`,
      change: routes.cart_change_url || `${fallbackRoot}cart/change.js`,
      update: routes.cart_update_url || `${fallbackRoot}cart/update.js`,
    };
    const response = await fetch(endpoints[action], {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const responseData = await response.json();
    if (!response.ok || responseData.status || responseData.errors) {
      throw new Error(responseData.description || responseData.message || responseData.errors || 'Der Warenkorb konnte nicht aktualisiert werden.');
    }
    return responseData;
  }

  async #fetchCart() {
    const cartUrl = window.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    const response = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Der Warenkorb konnte nicht geladen werden.');
    return response.json();
  }

  #eventResult(cart, sections, source) {
    return {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections, items: cart.items, itemCount: cart.item_count, source, didError: false },
    };
  }

  #sectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.add(component.dataset.sectionId);
    });
    if (this.dataset.sectionId) sectionIds.add(this.dataset.sectionId);
    return Array.from(sectionIds);
  }

  #itemHandle(item) {
    return String(item.handle || item.product_handle || '').split('?')[0];
  }

  #setCheckoutLocked(locked) {
    document.documentElement.classList.toggle('reparablo-cart-syncing', locked);
    this.#refreshCheckoutButtons();
  }

  #setLegalLocked(locked) {
    document.documentElement.classList.toggle('reparablo-legal-pending', locked);
    this.#refreshCheckoutButtons();
  }

  #refreshCheckoutButtons() {
    const syncing = document.documentElement.classList.contains('reparablo-cart-syncing');
    const legalPending = document.documentElement.classList.contains('reparablo-legal-pending');
    const locked = syncing || legalPending;

    document.querySelectorAll('button[name="checkout"], .cart__checkout-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (locked) {
        if (button.dataset.reparabloWasDisabled === undefined) {
          button.dataset.reparabloWasDisabled = button.disabled ? 'true' : 'false';
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.toggleAttribute('aria-busy', syncing);
      } else if (button.dataset.reparabloWasDisabled !== undefined) {
        button.disabled = button.dataset.reparabloWasDisabled === 'true';
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        delete button.dataset.reparabloWasDisabled;
      }
    });
  }

  #setLegalBusy(busy) {
    this.#legalBusy = busy;
    document.querySelectorAll('[data-legal-master], [data-legal-item]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.disabled = busy;
      input.toggleAttribute('aria-busy', busy);
    });
  }

  #clearLegalError() {
    document.querySelectorAll('[data-legal-error]').forEach((element) => {
      element.hidden = true;
    });
  }

  #showLegalError(error) {
    const fallback = this.querySelector('[data-legal-error]')?.dataset.defaultMessage || 'Bitte bestätige alle erforderlichen Punkte.';
    const message = error instanceof Error ? `${error.message} Bitte versuche es erneut.` : fallback;
    document.querySelectorAll('[data-legal-error]').forEach((element) => {
      element.textContent = message;
      element.hidden = false;
    });
    const consent = this.querySelector('[data-legal-consent]');
    consent?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #setFormBusy(form, busy) {
    form.querySelectorAll('button').forEach((button) => {
      button.disabled = busy;
      button.toggleAttribute('aria-busy', busy);
    });
  }

  #clearErrors() {
    this.querySelectorAll('[data-cart-services-error]').forEach((element) => {
      element.hidden = true;
      element.textContent = '';
    });
  }

  #showError(error, fallback) {
    const message = error instanceof Error ? error.message : fallback;
    this.querySelectorAll('[data-cart-services-error]').forEach((element) => {
      element.textContent = `${message || fallback} Bitte versuche es erneut.`;
      element.hidden = false;
    });
  }

  #dispatchCartError(error) {
    const message = error instanceof Error ? error.message : 'Der Warenkorb konnte nicht aktualisiert werden.';
    this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
  }
}

if (!customElements.get('cart-services')) customElements.define('cart-services', CartServices);
